import React, { useState, useEffect } from 'react';
import { X, Save, User, Briefcase, Calendar, MessageSquare, AlertCircle, ArrowRight, History, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

// ============================================================================
// SUBMISSION STATUSES - Juno Schema (Official)
// ============================================================================
// These match the SubmissionStatus class in backend/models.py (lines 3021-3075)
// DO NOT add custom statuses - use only these official Juno statuses
// ============================================================================

// Primary Pipeline Statuses (in typical progression order)
const SUBMISSION_STATUSES_PRIMARY = [
  'Submission',              // Initial submission
  'Client Submission',       // Submitted to client
  'Interview',               // In interview process
  'Offer Extended',          // Offer made to candidate
  'Offer Accepted',          // Candidate accepted offer
  'Onboarding',              // Pre-start onboarding
  'Onboarded'                // Candidate started work (completed placement)
];

// Terminal/Rejection Statuses
const SUBMISSION_STATUSES_TERMINAL = [
  'Offer Rejected',          // Candidate rejected offer
  'Interview Rejected',      // Failed interview or client rejected after interview
  'Rejected',                // General rejection (client or internal)
  'Rejected - Job Cancelled', // Job was cancelled
  'Rejected - Lost',         // Lost to competitor
  'Candidate Lost'           // Candidate withdrew or became unresponsive
];

// Combined list for dropdown
const SUBMISSION_STATUSES = [
  ...SUBMISSION_STATUSES_PRIMARY,
  '---', // Separator
  ...SUBMISSION_STATUSES_TERMINAL
];

const EditSubmissionStatusModal = ({ isOpen, onClose, submission, selectedUser, onSaveSuccess }) => {
  const [currentStatus, setCurrentStatus] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [statusChangeNotes, setStatusChangeNotes] = useState('');
  const [interviewDate, setInterviewDate] = useState('');
  const [statusHistory, setStatusHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch submission details and history when modal opens
  useEffect(() => {
    if (submission && isOpen) {
      // Set initial status from submission object (might be stale)
      const status = submission.status || submission.submission?.status || submission.submissionStatus || '';
      console.log('Initial status from submission object:', status);
      setCurrentStatus(status);
      setNewStatus(status);
      setInterviewDate(submission.submission?.interview_date || '');
      
      // Fetch fresh history which will update the current status
      fetchStatusHistory();
    }
  }, [submission, isOpen]);

  const fetchStatusHistory = async () => {
    if (!submission?.id) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/submissions/${submission.id}/history`);
      if (response.ok) {
        const data = await response.json();
        const history = data.history || [];
        setStatusHistory(history);
        
        // Update current status from API response (most accurate)
        if (data.current_status) {
          console.log('Setting current status from API:', data.current_status);
          setCurrentStatus(data.current_status);
          setNewStatus(data.current_status);
        } else if (history.length > 0) {
          // If no current_status in response, get from last history entry
          const latestStatus = history[history.length - 1].new_status || history[history.length - 1].to_status;
          console.log('Setting current status from history:', latestStatus);
          setCurrentStatus(latestStatus);
          setNewStatus(latestStatus);
        }
      } else {
        // If endpoint doesn't exist, try to get from submission object
        const history = submission.status_history || submission.submission?.status_history || [];
        setStatusHistory(history);
        
        // Get latest status from history
        if (history.length > 0) {
          const latestStatus = history[history.length - 1].new_status || history[history.length - 1].to_status;
          console.log('Setting current status from fallback history:', latestStatus);
          setCurrentStatus(latestStatus);
          setNewStatus(latestStatus);
        }
      }
    } catch (err) {
      console.error('Error fetching status history:', err);
      // Fallback to submission object
      const history = submission.status_history || submission.submission?.status_history || [];
      setStatusHistory(history);
      
      // Get latest status from history
      if (history.length > 0) {
        const latestStatus = history[history.length - 1].new_status || history[history.length - 1].to_status;
        console.log('Setting current status from error fallback:', latestStatus);
        setCurrentStatus(latestStatus);
        setNewStatus(latestStatus);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!newStatus) {
      setError('Please select a new status');
      return;
    }

    if (newStatus === currentStatus) {
      setError('New status must be different from current status');
      return;
    }

    if (!statusChangeNotes.trim()) {
      setError('Please provide notes explaining this status change');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Create status change log entry
      const statusChangeData = {
        new_status: newStatus,
        old_status: currentStatus,
        status_change_notes: statusChangeNotes,
        changed_by: selectedUser?.full_name || selectedUser?.name || 'Unknown User',
        changed_by_id: selectedUser?.id || null,
        changed_at: new Date().toISOString(),
        interview_date: interviewDate || null
      };

      // Update submission with new status
      const submissionResponse = await fetch(`${BACKEND_URL}/api/submissions/${submission.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          status_change_notes: statusChangeNotes,
          interview_date: interviewDate || null,
          status_history: [
            ...statusHistory,
            statusChangeData
          ]
        })
      });

      if (!submissionResponse.ok) {
        const errorData = await submissionResponse.json().catch(() => ({}));
        console.error('Server error response:', errorData);
        throw new Error(errorData.detail || `Failed to update submission status (${submissionResponse.status})`);
      }

      const result = await submissionResponse.json();
      console.log('Update successful:', result);

      // Success - trigger refresh and close
      onSaveSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating submission status:', err);
      const errorMsg = err.message || 'Failed to update submission status';
      setError(`${errorMsg}. Please check console for details.`);
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    if (SUBMISSION_STATUSES_PRIMARY.includes(status)) {
      const colors = {
        'Submission': 'bg-blue-100 text-blue-800',
        'Client Submission': 'bg-indigo-100 text-indigo-800',
        'Interview': 'bg-yellow-100 text-yellow-800',
        'Offer Extended': 'bg-purple-100 text-purple-800',
        'Offer Accepted': 'bg-green-100 text-green-800',
        'Onboarding': 'bg-teal-100 text-teal-800',
        'Onboarded': 'bg-emerald-100 text-emerald-800'
      };
      return colors[status] || 'bg-gray-100 text-gray-800';
    } else {
      // Terminal statuses - red/orange
      return 'bg-red-100 text-red-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      console.warn('No date provided to formatDate');
      return 'Date not available';
    }
    
    try {
      // Handle both ISO strings and Date objects
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return 'Invalid date';
      }
      
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      console.error('Error formatting date:', err, dateString);
      return String(dateString);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Edit Submission Status</h2>
            <p className="text-sm text-gray-600 mt-1">Update submission status and maintain audit trail</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Submission Info */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="font-medium text-gray-700">Candidate:</span>
                  <span className="text-gray-900 font-semibold">{submission?.candidateName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-gray-400" />
                  <span className="font-medium text-gray-700">Job:</span>
                  <span className="text-gray-900 font-semibold">{submission?.jobTitle}</span>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-red-900">Error</h4>
                  <p className="text-sm text-red-800 mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* Current State → Future State */}
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Change</h3>
              
              <div className="flex items-center gap-4">
                {/* Current State */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Status
                  </label>
                  <div className="flex items-center gap-2">
                    <span className={`px-4 py-3 rounded-lg font-semibold text-sm ${getStatusColor(currentStatus)}`}>
                      {currentStatus || 'Not set'}
                    </span>
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex items-center justify-center pt-6">
                  <ArrowRight className="h-6 w-6 text-gray-400" />
                </div>

                {/* Future State */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Status * <span className="text-xs text-gray-500 font-normal">(Juno Schema)</span>
                  </label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium"
                  >
                    <option value="">Select new status</option>
                    {SUBMISSION_STATUSES.map((status, index) => {
                      if (status === '---') {
                        return <option key={`sep-${index}`} disabled className="text-gray-400">─────────────────────</option>;
                      }
                      return <option key={status} value={status}>{status}</option>;
                    })}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Primary statuses track progression. Terminal statuses indicate rejection/withdrawal.
                  </p>
                </div>
              </div>
            </div>

            {/* Status Change Notes - Required */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Status Change Notes *
                </div>
              </label>
              <textarea
                value={statusChangeNotes}
                onChange={(e) => setStatusChangeNotes(e.target.value)}
                rows="3"
                placeholder="Explain why this status is changing (e.g., 'Client scheduled interview for next week', 'Candidate accepted offer verbally', etc.)..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">
                Required for audit trail. Explain the reason for this status change.
              </p>
            </div>

            {/* Interview Date - Conditional */}
            {(newStatus === 'Interview' || currentStatus === 'Interview') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Interview Date {newStatus === 'Interview' && <span className="text-red-600">*</span>}
                  </div>
                </label>
                <input
                  type="date"
                  value={interviewDate}
                  onChange={(e) => setInterviewDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Status History / Journey */}
            <div className="border-t-2 border-gray-200 pt-6">
              <div className="flex items-center gap-2 mb-4">
                <History className="h-5 w-5 text-gray-700" />
                <h3 className="text-lg font-semibold text-gray-900">Submission Journey (Status History)</h3>
              </div>

              {loading ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="h-8 w-8 animate-spin mx-auto mb-2" />
                  <p>Loading status history...</p>
                </div>
              ) : statusHistory.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-gray-500">
                  <History className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p>No status changes recorded yet.</p>
                  <p className="text-sm mt-1">This will be the first status change for this submission.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 border-b-2 border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Date & Time</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">From Status</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-700"></th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">To Status</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Changed By</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {statusHistory.map((entry, index) => {
                        // Check if this is the very first status (initial submission)
                        // Only consider it "first" if old_status is explicitly null/undefined, not if it's an empty string
                        const isFirstStatus = entry.old_status === null || entry.old_status === undefined;
                        const fromStatusDisplay = entry.old_status || entry.from_status || '—';
                        
                        return (
                          <tr key={index} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                              {formatDate(entry.changed_at || entry.date)}
                            </td>
                            <td className="px-4 py-3">
                              {isFirstStatus ? (
                                <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600 italic">
                                  —
                                </span>
                              ) : (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(fromStatusDisplay)}`}>
                                  {fromStatusDisplay}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <ArrowRight className="h-4 w-4 text-gray-400 mx-auto" />
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(entry.new_status || entry.to_status)}`}>
                                {isFirstStatus && (entry.new_status === 'Submission' || !entry.new_status) 
                                  ? 'New Submission' 
                                  : (entry.new_status || entry.to_status)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-700 font-medium">
                              {(() => {
                                const changedBy = entry.changed_by || entry.user || 'System';
                                // Check if it's a UUID pattern (8-4-4-4-12 characters)
                                const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                                if (uuidPattern.test(changedBy)) {
                                  // Try to get user name from changed_by_name field (if backend populated it)
                                  return entry.changed_by_name || 'User (legacy record)';
                                }
                                return changedBy;
                              })()}
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              {entry.status_change_notes || entry.notes || (isFirstStatus ? 'Initial submission created' : '—')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors font-medium"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !newStatus || newStatus === currentStatus || !statusChangeNotes.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <Clock className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Status Change
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditSubmissionStatusModal;